import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/Modal';
import { Button } from '@/components/Button';
import { apiPostRequest } from '@/utils/apiClient';

const getOrCreateSessionId = (): string => {
    const storageKey = 'kotomil_session_id';
    let sessionId = sessionStorage.getItem(storageKey);
    if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem(storageKey, sessionId);
    }
    return sessionId;
};

export const logBookmarkGuideEvent = async () => {
    try {
        const sessionId = getOrCreateSessionId();
        await apiPostRequest('/api/log/event', {
            sessionId,
            eventType: 'onboarding',
            eventKey: 'bookmark_guide',
            metadata: {},
        });
    } catch (error) {
        console.error('ブックマーク案内モーダルのイベントログ送信に失敗しました', error);
    }
};

export interface BookmarkGuideModalProps {
    isOpen: boolean;
    onClose: () => void;
    isMobile: boolean;
}

/**
 * ブックマーク案内モーダル（チュートリアル完了後の分析フロー用）
 */
const BookmarkGuideModal: React.FC<BookmarkGuideModalProps> = ({ isOpen, onClose, isMobile }) => {
    const { t } = useTranslation('onboarding');

    const mobileContent = t('steps.habit.mobile', { returnObjects: true }) as Record<string, string>;
    const desktopContent = t('steps.habit.desktop', { returnObjects: true }) as Record<string, string>;

    return (
        <Modal isOpen={isOpen} onClose={onClose} contentClassName="w-full max-w-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-center">{t('steps.habit.title')}</h2>

            <div className="mb-6" dangerouslySetInnerHTML={{ __html: t('steps.habit.recommendation') }} />

            {isMobile ? (
                <div className="space-y-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-xl mb-2">{mobileContent.bookmarkHeading}</h3>
                        <p>{mobileContent.bookmarkDescription}</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-xl mb-2">{mobileContent.appHeading}</h3>
                        <h4 className="font-semibold pt-2">{mobileContent.chromeHeading}</h4>
                        <p>{mobileContent.chromeDescription}</p>
                        <h4 className="font-semibold pt-2">{mobileContent.safariHeading}</h4>
                        <p>{mobileContent.safariDescription}</p>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-2">{desktopContent.bookmarkHeading}</h3>
                        <p
                            className="text-gray-700"
                            dangerouslySetInnerHTML={{ __html: desktopContent.bookmarkDescription }}
                        />
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-2">{desktopContent.appHeading}</h3>
                        <p className="text-gray-700 mb-1">{desktopContent.chromeHeading}</p>
                        <p
                            className="text-gray-700 mb-3"
                            dangerouslySetInnerHTML={{ __html: desktopContent.chromeDescription }}
                        />
                        {desktopContent.edgeHeading && (
                            <>
                                <p className="text-gray-700 mb-1 font-semibold">{desktopContent.edgeHeading}</p>
                                <p
                                    className="text-gray-700"
                                    dangerouslySetInnerHTML={{ __html: desktopContent.edgeDescription }}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="mt-8 flex justify-center">
                <Button
                    variant="outline"
                    className="px-8 py-2 border-2 border-black rounded-lg min-w-[120px]"
                    onClick={onClose}
                >
                    {t('steps.habit.cta')}
                </Button>
            </div>
        </Modal>
    );
};

export default BookmarkGuideModal;
